import { ChangeDetectionStrategy, Component, Input, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { NgChartsModule } from 'ng2-charts';
import { Chart, ArcElement, Tooltip, Legend, ChartOptions, ChartData } from 'chart.js';
import { MatDividerModule } from '@angular/material/divider';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { TaskStatsService } from '../services/task-stats.service';
import { TaskStatsResponse } from '@app/lib/models/task-stats.model';

type Submenu = { id: string; title: string };

Chart.register(ArcElement, Tooltip, Legend, DataLabelsPlugin);

@Component({
  selector: 'app-submenu-accordion',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    NgChartsModule,
    MatDividerModule
  ],
  templateUrl: './submenu-accordion.component.html',
  styleUrls: ['./submenu-accordion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubmenuAccordionComponent implements OnInit {
  @Input() submenus: Submenu[] | null = null;

  private readonly DEFAULT_SUBMENUS: ReadonlyArray<Submenu> = [
    { id: 'task-monitor', title: 'Task Monitor' },
  ];

  private api = inject(TaskStatsService);
  private route = inject(ActivatedRoute);

  // State
  loaded  = signal<Record<string, boolean>>({});
  loading = signal<Record<string, boolean>>({});
  error   = signal<Record<string, string | null>>({});
  data    = signal<Record<string, TaskStatsResponse>>({});

 
  // Config
  slaDays = 2; 

  // Professional Color Palette
  private readonly COLORS = {
    completed: '#10b981', // Emerald 500
    pending:   '#cbd5e1', // Slate 300 (or use #f43f5e for warning)
    hover:     ['#059669', '#94a3b8']
  };

  pieOptions: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false, // Important for CSS grid control
    layout: {
        padding: 20
    },
    plugins: {
      legend: { 
        display: true, 
        position: 'bottom',
        labels: { usePointStyle: true, padding: 15, font: { size: 11 } } 
      },
      tooltip: { enabled: true }, // Re-enabled for better UX
      datalabels: {
        display: (ctx) => {
           const val = ctx.dataset.data[ctx.dataIndex] as number;
           // Only show label if slice is > 5% to avoid clutter
           const total = (ctx.dataset.data as number[]).reduce((a, b) => a + b, 0);
           return total > 0 && (val / total > 0.05);
        },
        formatter: (value, ctx) => {
          const data  = ctx.dataset.data as number[];
          const total = data.reduce((a, b) => a + (Number(b) || 0), 0);
          const pct   = total ? (Number(value) / total) * 100 : 0;
          return `${pct.toFixed(0)}%`;
        },
        color: '#fff',
        font: { weight: 'bold', size: 12 },
        anchor: 'center',
        align: 'center',
      }
    }
  };

  ngOnInit(): void {
    if (!this.submenus || this.submenus.length === 0) {
      const fromRoute = (this.route.snapshot.data['submenus'] as Submenu[] | undefined) ?? [];
      this.submenus = fromRoute.length ? fromRoute : [...this.DEFAULT_SUBMENUS];
    }
  }

  trackById = (_: number, s: Submenu) => s.id;

  onOpened(submenuId: string) {
    if (this.loaded()[submenuId] || this.loading()[submenuId]) return;
    this.setLoading(submenuId, true);
    this.setError(submenuId, null);

    this.api.fetchStats().subscribe({
      next: (res) => {
        const cache = { ...this.data() };
        cache[submenuId] = res;
        this.data.set(cache);
        this.setLoaded(submenuId, true);
        this.setLoading(submenuId, false);
      },
      error: (err) => {
        this.setError(submenuId, err?.message || 'Failed to load data');
        this.setLoading(submenuId, false);
      }
    });
  }

  // Helpers
  private setLoading(id: string, v: boolean) { const m = { ...this.loading() }; m[id] = v; this.loading.set(m); }
  private setLoaded (id: string, v: boolean) { const m = { ...this.loaded()  }; m[id] = v; this.loaded.set(m); }
  private setError  (id: string, v: string | null) { const m = { ...this.error() }; m[id] = v; this.error.set(m); }

  buildL3MonthsPie(res: TaskStatsResponse): ChartData<'pie'> {
    return {
      labels: ['Completed', 'Pending'],
      datasets: [{
        data: [
          res?.lastThreeMonthsCompletedTasks ?? 0,
          res?.lastThreeMonthsNotCompletedTasks ?? 0
        ],
        backgroundColor: [this.COLORS.completed, this.COLORS.pending],
        hoverBackgroundColor: this.COLORS.hover,
        borderWidth: 0
      }]
    };
  }

  buildYesterdayPie(res: TaskStatsResponse): ChartData<'pie'> {
    return {
      labels: ['Completed', 'Pending'],
      datasets: [{
        data: [
          res?.fromYesterdayCompletedTasks ?? 0,
          res?.fromYesterdayNotCompletedTasks ?? 0
        ],
        backgroundColor: [this.COLORS.completed, this.COLORS.pending],
        hoverBackgroundColor: this.COLORS.hover,
        borderWidth: 0
      }]
    };
  }

  l3Total(res: TaskStatsResponse): number {
    return (res?.lastThreeMonthsCompletedTasks ?? 0) + (res?.lastThreeMonthsNotCompletedTasks ?? 0);
  }

  yTotal(res: TaskStatsResponse): number {
    return (res?.fromYesterdayCompletedTasks ?? 0) + (res?.fromYesterdayNotCompletedTasks ?? 0);
  }



  overallAvgDays(res: TaskStatsResponse): number {
    const v = res?.tasksStatsAvgDurationInDays;
    if (typeof v !== 'number' || Number.isNaN(v)) return 0;
    return Math.round(v * 10) / 10;
  }

  gaugeValue(res: TaskStatsResponse): number {
    // Limit to 100 for the gauge visual
    const val = this.overallAvgDays(res);
    // If value is 0, return 0 (empty gauge)
    if (!val) return 0;
    
    // Calculate % relative to SLA. 
    // If SLA is 2 days and val is 1, we are at 50% of SLA (Good).
    // However, for a gauge, usually "Full" means "Bad" (taking too long) or "Good" (100% done).
    // Assuming here: 0 days = 0% filled. SLA days = 100% filled.
    const pct = (val / this.slaDays) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  }

  formatSeconds(sec?: number): string {
    const s = Number(sec ?? 0);
    if (!Number.isFinite(s) || s <= 0) return '0s';
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    return [d ? `${d}d` : '', h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ') || '< 1m';
  }
}